import { AxiosResponse } from 'axios';
import { applyOrgFilter } from '@/lib/activeOrg';
import { mapFxInvoice } from '@/lib/mappers';
import { filterByIdOrShortId } from '@/lib/shortId';
import { supabase } from '@/lib/supabase';
import {
  asResponse,
  paginationRange,
  buildPaginatedResponse,
  throwIfError,
} from '@/lib/supabase-helpers';
import { ResponseWithPagination } from '@/types';
import { FxInvoice, FxInvoiceStatus } from '@/types/api/finance';

const SELECT_WITH_JOINS = '*, fx_vendors(*, fx_categories(*))';

export type FxInvoiceSummary = {
  totalInvoiced: number;
  totalPaid: number;
  totalOutstanding: number;
  totalOverdue: number;
  countByStatus: Record<FxInvoiceStatus, number>;
};

export type FxClientInvoiceSummary = {
  clientName: string;
  teamId: string | null;
  totalInvoiced: number;
  totalPaid: number;
  outstanding: number;
  invoiceCount: number;
};

export type FxAgingBracket = {
  bracket: string;
  count: number;
  amount: number;
};

/** One unpaid invoice, for the cash-in outlook. */
export type FxOpenReceivable = {
  id: string;
  shortId: string | null;
  invoiceNumber: string;
  clientName: string;
  openBalance: number;
  dueDate: string | null;
};

export type FxInvoiceLine = {
  id: string;
  invoiceId: string;
  externalId: string | null;
  description: string | null;
  quantity: number | null;
  unitPrice: number | null;
  amount: number;
  profileId: string | null;
  profileName: string | null;
  itemName: string | null;
  createdAt: string;
};

function mapInvoiceLine(row: any): FxInvoiceLine {
  return {
    id: row.id,
    invoiceId: row.invoice_id,
    externalId: row.external_id,
    description: row.description,
    quantity: row.quantity ? Number(row.quantity) : null,
    unitPrice: row.unit_price ? Number(row.unit_price) : null,
    amount: Number(row.amount),
    profileId: row.profile_id,
    profileName: row.profile_name,
    itemName: row.item_name,
    createdAt: row.created_at,
  };
}

const financeInvoicesService = {
  async getAll({
    page,
    take,
    status,
    clientName,
    startDate,
    endDate,
  }: {
    page: number;
    take: number;
    status?: FxInvoiceStatus;
    clientName?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<AxiosResponse<ResponseWithPagination<FxInvoice[]>>> {
    const [from, to] = paginationRange(page, take);
    let query = supabase
      .from('fx_invoices')
      .select(SELECT_WITH_JOINS, { count: 'exact' })
      .order('date', { ascending: false })
      .order('invoice_number', { ascending: false })
      .range(from, to);

    query = applyOrgFilter(query);
    if (status) query = query.eq('status', status);
    if (clientName) query = query.eq('client_name', clientName);
    if (startDate) query = query.gte('date', startDate);
    if (endDate) query = query.lte('date', endDate);

    const result = await query;
    throwIfError(result);
    const mapped = (result.data ?? []).map(mapFxInvoice);
    return asResponse(buildPaginatedResponse(mapped, result.count ?? 0, page, take));
  },

  async getSummary(options?: {
    startDate?: string;
    endDate?: string;
  }): Promise<AxiosResponse<FxInvoiceSummary>> {
    let query = supabase.from('fx_invoices').select('amount, open_balance, status');
    query = applyOrgFilter(query);
    if (options?.startDate) query = query.gte('date', options.startDate);
    if (options?.endDate) query = query.lte('date', options.endDate);
    const { data, error } = await query;
    if (error) throw error;

    const rows = data ?? [];
    let totalInvoiced = 0;
    let totalPaid = 0;
    let totalOutstanding = 0;
    let totalOverdue = 0;
    const countByStatus: Record<string, number> = { paid: 0, partial: 0, open: 0, overdue: 0 };

    rows.forEach((r: any) => {
      const amount = parseFloat(r.amount);
      const balance = parseFloat(r.open_balance ?? '0');
      totalInvoiced += amount;
      totalPaid += amount - balance;
      totalOutstanding += balance;
      if (r.status === 'overdue') totalOverdue += balance;
      countByStatus[r.status] = (countByStatus[r.status] || 0) + 1;
    });

    return asResponse({
      totalInvoiced,
      totalPaid,
      totalOutstanding,
      totalOverdue,
      countByStatus: countByStatus as Record<FxInvoiceStatus, number>,
    });
  },

  async getByClient(): Promise<AxiosResponse<FxClientInvoiceSummary[]>> {
    let query = supabase
      .from('fx_invoices')
      .select('client_name, team_id, amount, open_balance, status');
    query = applyOrgFilter(query);
    const { data, error } = await query;
    if (error) throw error;

    const map = new Map<string, FxClientInvoiceSummary>();

    (data ?? []).forEach((r: any) => {
      const amount = parseFloat(r.amount);
      const balance = parseFloat(r.open_balance ?? '0');
      const existing = map.get(r.client_name) ?? {
        clientName: r.client_name,
        teamId: r.team_id,
        totalInvoiced: 0,
        totalPaid: 0,
        outstanding: 0,
        invoiceCount: 0,
      };
      existing.totalInvoiced += amount;
      existing.totalPaid += amount - balance;
      existing.outstanding += balance;
      existing.invoiceCount += 1;
      map.set(r.client_name, existing);
    });

    return asResponse(Array.from(map.values()).sort((a, b) => b.totalInvoiced - a.totalInvoiced));
  },

  async getAgingReport(): Promise<AxiosResponse<FxAgingBracket[]>> {
    let query = supabase
      .from('fx_invoices')
      .select('due_date, open_balance, status')
      .gt('open_balance', 0);
    query = applyOrgFilter(query);
    const { data, error } = await query;
    if (error) throw error;

    const today = new Date();
    const brackets: FxAgingBracket[] = [
      { bracket: 'Current', count: 0, amount: 0 },
      { bracket: '1-30 days', count: 0, amount: 0 },
      { bracket: '31-60 days', count: 0, amount: 0 },
      { bracket: '61-90 days', count: 0, amount: 0 },
      { bracket: '90+ days', count: 0, amount: 0 },
    ];

    (data ?? []).forEach((r: any) => {
      const balance = parseFloat(r.open_balance) || 0;
      if (!r.due_date) {
        brackets[0].count += 1;
        brackets[0].amount += balance;
        return;
      }
      const due = new Date(r.due_date);
      const daysOverdue = Math.floor((today.getTime() - due.getTime()) / (1000 * 60 * 60 * 24));

      let idx = 0;
      if (daysOverdue <= 0) idx = 0;
      else if (daysOverdue <= 30) idx = 1;
      else if (daysOverdue <= 60) idx = 2;
      else if (daysOverdue <= 90) idx = 3;
      else idx = 4;

      brackets[idx].count += 1;
      brackets[idx].amount += balance;
    });

    return asResponse(brackets);
  },

  /**
   * Every unpaid invoice, deliberately NOT filtered by the page's date range.
   *
   * The date filter bounds invoice *issue* date and caps at today, but the cash
   * outlook is about when money arrives — due dates run into the future, and an
   * invoice issued in January can still be owed. Reusing the filtered list would
   * silently drop receivables from the forecast.
   */
  async getOpenReceivables(): Promise<FxOpenReceivable[]> {
    let query = supabase
      .from('fx_invoices')
      .select('id, short_id, invoice_number, client_name, open_balance, due_date')
      .gt('open_balance', 0)
      .order('due_date', { ascending: true });
    query = applyOrgFilter(query);

    const { data, error } = await query;
    if (error) throw error;

    return (data ?? []).map((r: any) => ({
      id: r.id,
      shortId: r.short_id ?? null,
      invoiceNumber: r.invoice_number,
      clientName: r.client_name,
      openBalance: parseFloat(r.open_balance) || 0,
      dueDate: r.due_date,
    }));
  },

  async getById(id: string): Promise<FxInvoice> {
    let query = supabase
      .from('fx_invoices')
      .select(SELECT_WITH_JOINS);
    query = applyOrgFilter(query);
    query = filterByIdOrShortId(query, id);
    const { data, error } = await query.single();
    if (error) throw error;
    return mapFxInvoice(data);
  },

  async getLines(invoiceId: string): Promise<FxInvoiceLine[]> {
    const { data, error } = await supabase
      .from('fx_invoice_lines')
      .select('*')
      .eq('invoice_id', invoiceId)
      .order('amount', { ascending: false });
    if (error) throw error;
    return (data ?? []).map(mapInvoiceLine);
  },

  async updateLineProfile(
    lineId: string,
    profileId: string | null,
    profileName: string | null
  ): Promise<void> {
    const { error } = await supabase
      .from('fx_invoice_lines')
      .update({ profile_id: profileId, profile_name: profileName })
      .eq('id', lineId);
    if (error) throw error;
  },

  async update(
    id: string,
    payload: {
      status?: FxInvoiceStatus;
      openBalance?: number;
      matchedTransactionId?: string | null;
      memo?: string;
      billingPeriod?: string | null;
    }
  ): Promise<AxiosResponse<FxInvoice>> {
    const updateData: any = { updated_at: new Date().toISOString() };
    if (payload.status !== undefined) updateData.status = payload.status;
    if (payload.openBalance !== undefined) updateData.open_balance = payload.openBalance;
    if (payload.matchedTransactionId !== undefined)
      updateData.matched_transaction_id = payload.matchedTransactionId;
    if (payload.memo !== undefined) updateData.memo = payload.memo;
    if (payload.billingPeriod !== undefined) updateData.billing_period = payload.billingPeriod;

    let updateQuery = supabase
      .from('fx_invoices')
      .update(updateData)
      .eq('id', id)
      .select(SELECT_WITH_JOINS);
    updateQuery = applyOrgFilter(updateQuery);
    const result = await updateQuery.single();
    throwIfError(result);
    return asResponse(mapFxInvoice(result.data));
  },
};

export default financeInvoicesService;
